import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  useCallback,
  useContext,
  useEffect,
  useState,
  createContext,
} from 'react';

import { OxfordHelpers } from './Helpers';

const otpScriptUrl =
  'https://cdnx.beenverified.com/assets/integrations/otp_loader.js';
const _delayRequest = 3000;

interface CustomWindow extends Window {
  IGLOO?: {
    getBlackbox: () => {
      finished: boolean;
      blackbox: string;
    };
  };
}

interface OneTimePasscodeContextType {
  blackBoxId: string | undefined;
  retryBlackBoxId: () => Promise<string>;
}

export const OneTimePasscodeContext = createContext<
  OneTimePasscodeContextType | undefined
>(undefined);

export const useOneTimePasscodeContext = () => {
  const context = useContext(OneTimePasscodeContext);

  if (context === undefined) {
    throw new Error(
      'useOneTimePasscodeContext must be used within a OneTimePasscodeProvider'
    );
  }

  return context;
};

export const OneTimePasscodeProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [blackBoxId, setBlackBoxId] = useState<string | undefined>();
  const [isLoaded, setIsLoaded] = useState(false);

  const { logError } = useContext(AppConfig);

  const removeExistingScript = () => {
    const existing = document.querySelector(`script[src="${otpScriptUrl}"]`);
    if (existing) {
      existing.remove();
    }
    delete (window as CustomWindow).IGLOO;
  };

  const loadScript = (forceReload = false) => {
    if (forceReload) {
      removeExistingScript();
      setIsLoaded(false);
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = otpScriptUrl;
      document.head.appendChild(script);

      script.addEventListener('load', () => {
        setIsLoaded(true);
        resolve(script);
      });

      script.addEventListener('error', () => {
        reject(new Error(`${otpScriptUrl} failed to load.`));
      });
    });
  };

  const load = async () => {
    if (!isLoaded) {
      try {
        await loadScript();
      } catch (e: unknown) {
        if (e instanceof Error) {
          logError('Error loading one time password script', e);
        }
      }
    }
  };

  const getBlackBoxId = async () => {
    let attempts = 0;
    const maxAttempts = 2;

    while (attempts < maxAttempts) {
      try {
        const bb = (window as CustomWindow)?.IGLOO?.getBlackbox();

        if (bb?.finished) {
          return bb.blackbox as string;
        }
        attempts += 1;
        await OxfordHelpers.wait(_delayRequest);
      } catch (_e) {
        attempts += 1;
        await OxfordHelpers.wait(_delayRequest);
      }
    }

    return '';
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    async function storeBlackBoxId() {
      const bb = await getBlackBoxId();
      setBlackBoxId(bb);
    }

    storeBlackBoxId();
  }, [isLoaded]);

  const retryBlackBoxId = useCallback(async () => {
    if (blackBoxId) {
      return blackBoxId;
    }

    try {
      await loadScript(true);
    } catch (e: unknown) {
      if (e instanceof Error) {
        logError('Error reloading one time password script on retry', e);
      }
      return '';
    }

    const bb = await getBlackBoxId();
    setBlackBoxId(bb);
    return bb;
  }, [blackBoxId]);

  return (
    <OneTimePasscodeContext.Provider value={{ blackBoxId, retryBlackBoxId }}>
      {children}
    </OneTimePasscodeContext.Provider>
  );
};
